import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiUser, FiMenu, FiX } from "react-icons/fi";
import logo from "../assets/testx_logo.jpeg";

// Section ids on the Home page. "choose-assessment" must match the id on
// TestsSection.jsx's outer container; "about" must match an id on
// AboutSection.jsx's outer <section> (add one if it doesn't have it yet).
const SECTION_IDS = {
  home: null, // no id needed — "Home" just scrolls to the very top
  about: "about",
  tests: "choose-assessment",
};

// Order matters here — used to figure out which section is active while
// scrolling (checked top to bottom, "home" is just "nothing else matched").
const SCROLL_SECTIONS = ["about", "tests"];

function Header({ isLoggedIn, showLogin, goProfile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  const scrollToTarget = (targetId) => {
    if (!targetId) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // After navigating here from another page with a pending scrollTarget,
  // wait a tick for the Home page's sections to mount, then scroll.
  useEffect(() => {
    if (location.pathname === "/" && location.state?.scrollTarget) {
      const targetId = SECTION_IDS[location.state.scrollTarget];
      const timer = setTimeout(() => scrollToTarget(targetId), 100);

      // Clear the scrollTarget from history state so refreshing or
      // navigating back doesn't re-trigger the scroll.
      window.history.replaceState({}, document.title);

      return () => clearTimeout(timer);
    }
  }, [location]);

  // Track which section is currently in view so we can show a green
  // active-page indicator under the matching nav link.
  const updateActiveSection = useCallback(() => {
    if (location.pathname !== "/") return;

    const scrollPos = window.scrollY + 120; // offset for the fixed header

    if (window.scrollY < 80) {
      setActiveSection("home");
      return;
    }

    let current = "home";
    for (const key of SCROLL_SECTIONS) {
      const el = document.getElementById(SECTION_IDS[key]);
      if (el && el.offsetTop <= scrollPos) {
        current = key;
      }
    }
    setActiveSection(current);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveSection(null); // no nav item is "active" off the home page
      return;
    }
    updateActiveSection();
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    return () => window.removeEventListener("scroll", updateActiveSection);
  }, [location.pathname, updateActiveSection]);

  const scrollToSection = (section) => {
    setIsMenuOpen(false);

    if (location.pathname === "/") {
      // Already on the home page — scroll immediately, no navigation needed.
      scrollToTarget(SECTION_IDS[section]);
      setActiveSection(section);
    } else {
      navigate("/", { state: { scrollTarget: section } });
    }
  };

  const handleProfile = () => {
    setIsMenuOpen(false);
    goProfile();
  };

  const handleLogin = () => {
    setIsMenuOpen(false);
    showLogin();
  };

  return (
    <>
      <header className="tx-header">
        <div className="tx-header__inner">
          <div className="tx-header__logo" onClick={() => scrollToSection("home")}>
            <span className="tx-header-logo-crop">
              <img src={logo} alt="TestX" className="tx-header-logo-img" />
            </span>
            <span className="tx-header-logo-text">TestX</span>
          </div>

          <div className="tx-header__right">
            <nav className="tx-header__nav">
              <div className="tx-nav-links">
                <button
                  className={activeSection === "home" ? "tx-active" : ""}
                  onClick={() => scrollToSection("home")}
                >
                  Home
                </button>
                <button
                  className={activeSection === "about" ? "tx-active" : ""}
                  onClick={() => scrollToSection("about")}
                >
                  About
                </button>
                <button
                  className={activeSection === "tests" ? "tx-active" : ""}
                  onClick={() => scrollToSection("tests")}
                >
                  Tests
                </button>
                <button onClick={() => window.open("https://wiztest.org/", "_blank")}>
                  Affordable Tests
                </button>
              </div>
            </nav>

            <div className="tx-header-actions">
              {isLoggedIn ? (
                <button className="tx-profile-btn" onClick={handleProfile}>
                  <FiUser size={16} /> Profile
                </button>
              ) : (
                <button className="tx-login-btn" onClick={handleLogin}>
                  Login / Sign Up
                </button>
              )}
            </div>
          </div>

          <button
            className="tx-mobile-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>
      </header>

      <div className={`tx-mobile-menu ${isMenuOpen ? "tx-open" : ""}`}>
          <div className="tx-mobile-content">
            <button
              className={activeSection === "home" ? "tx-active" : ""}
              onClick={() => scrollToSection("home")}
            >
              Home
            </button>
            <button
              className={activeSection === "about" ? "tx-active" : ""}
              onClick={() => scrollToSection("about")}
            >
              About
            </button>
            <button
              className={activeSection === "tests" ? "tx-active" : ""}
              onClick={() => scrollToSection("tests")}
            >
              Tests
            </button>
            <button onClick={() => window.open("https://wiztest.org/", "_blank")}>
              Affordable Tests
            </button>

            {isLoggedIn ? (
              <button onClick={handleProfile}>View Profile</button>
            ) : (
              <button onClick={handleLogin}>Login / Sign Up</button>
            )}
          </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        /* ===== HEADER ===== */
        .tx-header {
          display: block !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 84px;
          background: rgba(255, 255, 255, 0.88) !important;
          backdrop-filter: saturate(180%) blur(12px);
          -webkit-backdrop-filter: saturate(180%) blur(12px);
          border-bottom: 1px solid rgba(226, 232, 240, 0.8);
          box-shadow: 0 1px 12px rgba(15, 23, 42, 0.04);
          z-index: 1000;
          box-sizing: border-box;
          font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        .tx-header__inner {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between;
          width: 100% !important;
          max-width: 1400px;
          height: 100%;
          margin: 0 auto;
          padding: 0 48px;
          gap: 24px;
          box-sizing: border-box;
        }

        .tx-header__logo {
          display: flex !important;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          flex-shrink: 0;
        }

        .tx-header-logo-crop {
          height: 76px;
          width: 76px;
          display: block;
          overflow: visible;
          flex-shrink: 0;
        }

        .tx-header-logo-img {
          display: block;
          height: 100%;
          width: 100%;
          object-fit: contain;
          object-position: center;
        }

        .tx-header-logo-text {
          font-family: "Sora", "Segoe UI", sans-serif;
          font-size: 24px;
          font-weight: 800;
          letter-spacing: -0.5px;
          line-height: 1;
          color: #0f172a;
        }

        /* Groups nav + actions together on the right, next to Profile/Login */
        .tx-header__right {
          display: flex;
          align-items: center;
          gap: 48px;
          min-width: 0;
        }

        .tx-header__nav {
          display: flex;
          align-items: center;
        }

        .tx-nav-links {
          display: flex;
          align-items: center;
          gap: 40px;
          background: transparent;
          border: none;
          border-radius: 0;
          padding: 0;
        }

        .tx-nav-links button {
          position: relative;
          background: transparent;
          border: none;
          outline: none;
          box-shadow: none;
          color: #1e293b;
          font-family: inherit;
          font-size: 16px;
          font-weight: 600;
          letter-spacing: 0.2px;
          cursor: pointer;
          padding: 4px 0;
          border-radius: 0;
          transition: color 0.2s ease;
        }

        .tx-nav-links button::after {
          content: "";
          position: absolute;
          left: 50%;
          bottom: -6px;
          width: 0;
          height: 3px;
          border-radius: 3px;
          background: linear-gradient(90deg, #34d399, #10b981);
          transform: translateX(-50%);
          transition: width 0.25s ease;
        }

        .tx-nav-links button:hover {
          color: #10b981;
          background: transparent;
        }

        .tx-nav-links button:hover::after {
          width: 60%;
        }

        .tx-nav-links button.tx-active {
          color: #10b981;
          font-weight: 700;
        }

        .tx-nav-links button.tx-active::after {
          width: 100%;
        }

        .tx-login-btn,
        .tx-profile-btn {
          background: #10b981 !important;
          color: #ffffff !important;
          border: none !important;
          padding: 12px 28px !important;
          border-radius: 999px !important;
          font-family: inherit;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.1px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 4px 14px -2px rgba(16, 185, 129, 0.4);
          transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
          flex-shrink: 0;
        }

        .tx-login-btn:hover,
        .tx-profile-btn:hover {
          background: #0ea371 !important;
          box-shadow: 0 6px 18px -2px rgba(16, 185, 129, 0.45);
          transform: translateY(-1px);
        }

        .tx-mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          color: #0f172a;
          cursor: pointer;
          padding: 10px;
        }

        .tx-mobile-menu {
          position: fixed;
          top: 84px;
          right: -100%;
          width: 280px;
          height: calc(100vh - 84px);
          background: #ffffff !important;
          transition: right 0.3s ease;
          padding: 30px 24px;
          box-shadow: -8px 0 24px rgba(15, 23, 42, 0.08);
          z-index: 999;
          overflow-y: auto;
          font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        .tx-mobile-menu.tx-open {
          right: 0;
        }

        .tx-mobile-content {
          display: flex;
          flex-direction: column;
          gap: 22px;
        }

        .tx-mobile-content button {
          position: relative;
          background: transparent;
          border: none;
          color: #334155;
          font-family: inherit;
          font-size: 17px;
          font-weight: 600;
          text-align: left;
          cursor: pointer;
          padding: 8px 0 8px 14px;
        }

        .tx-mobile-content button.tx-active {
          color: #0f172a;
          font-weight: 600;
        }

        @media (max-width: 900px) {
          .tx-header__inner {
            padding: 0 24px;
          }

          .tx-header__right {
            display: none;
          }

          .tx-mobile-menu-btn {
            display: block;
          }
        }

        @media (max-width: 480px) {
          .tx-header__inner {
            padding: 0 16px;
          }

          .tx-header-logo-text {
            font-size: 19px;
          }

          .tx-header-logo-crop {
            height: 54px;
            width: 54px;
          }
        }
      `}</style>
    </>
  );
}

export default Header;